import * as React from "react";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "shared/components/FormElements/Button";

const AutoWizard = React.forwardRef(
  (
    {
      // Step configuration
      steps = [],
      stepContent = [],

      // Callbacks
      onStepChange,
      onComplete,
      onReset,

      // Customization
      optionalSteps = [],
      backButtonText = "Back",
      nextButtonText = "Next",
      finishButtonText = "Finish",
      optionalText = "Optional",

      // Styling
      stepSx = {},
      buttonSx = {},

      // Initial state
      initialStep = 0,
      isProcessing = false, // New prop for processing state
    },
    ref
  ) => {
    const [activeStep, setActiveStep] = React.useState(initialStep);
    const [canProceed, setCanProceed] = React.useState(false);

    const isStepOptional = (step) => {
      return optionalSteps.includes(step);
    };

    const handleNext = () => {
      if (!canProceed || isProcessing) return; // Don't proceed if validation fails or processing

      const nextStep = activeStep + 1;
      setActiveStep(nextStep);

      if (onStepChange) {
        onStepChange(nextStep, { activeStep });
      }
    };

    const handleBack = () => {
      if (isProcessing) return; // Don't proceed if processing

      const prevStep = activeStep - 1;
      setActiveStep(prevStep);

      if (onStepChange) {
        onStepChange(prevStep, { activeStep: prevStep });
      }
    };

    const handleReset = () => {
      setActiveStep(0);
      setCanProceed(false);

      if (onReset) {
        onReset();
      }
    };

    const handleComplete = () => {
      if (onComplete) {
        onComplete({ activeStep });
      }
    };

    const updateCanProceed = (canProceed) => {
      setCanProceed(canProceed);
    };

    // Expose handleNext to parent via ref
    React.useImperativeHandle(ref, () => ({
      handleNext,
      handleBack,
      handleReset,
      handleComplete,
      updateCanProceed,
      activeStep,
      canProceed,
    }));

    const renderStepContent = () => {
      if (activeStep < stepContent.length) {
        const content = stepContent[activeStep];

        // If content is a function, render it as a React component to keep hook rules intact
        if (typeof content === "function") {
          const StepContentComponent = content;
          return (
            <StepContentComponent
              activeStep={activeStep}
              isStepOptional={isStepOptional(activeStep)}
              handleNext={handleNext}
              handleBack={handleBack}
              handleComplete={handleComplete}
              handleReset={handleReset}
              updateCanProceed={updateCanProceed}
              canProceed={canProceed}
              isProcessing={isProcessing}
            />
          );
        }

        // If content is a React element, render it directly
        return content;
      }

      // Default content if no step content provided
      return (
        <Card sx={{ height: "100%", maxHeight: "100%" }}>
          <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" gutterBottom>
              Step {activeStep + 1}
            </Typography>
            <Typography sx={{ flex: 1 }}>{steps[activeStep] || `Step ${activeStep + 1} content`}</Typography>
          </CardContent>
        </Card>
      );
    };

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          marginTop: "10px",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <div style={{ width: "75%", margin: "0 auto" }}>
            <Stepper
              activeStep={activeStep}
              sx={{
                ...stepSx,
                "& .MuiStepIcon-root.Mui-active": {
                  color: "#4caf50", // Sadece active step yeşil
                },
              }}
            >
              {steps.map((label, index) => {
                const stepProps = {};
                const labelProps = {};

                if (isStepOptional(index)) {
                  labelProps.optional = <Typography variant="caption">{optionalText}</Typography>;
                }

                return (
                  <Step key={label} {...stepProps}>
                    <StepLabel {...labelProps}>{label}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              maxHeight: "100%",
              marginBottom: "40px",
              marginTop: "30px",
              paddingLeft: "30px",
              paddingRight: "30px",
            }}
          >
            {renderStepContent()}
          </div>
        </div>

        <div className="footer-container" style={{ paddingLeft: "30px", paddingRight: "30px" }}>
          <Button
            inverse
            disabled={activeStep === 0 || isProcessing}
            onClick={handleBack}
            style={{ marginRight: "0.5rem", ...buttonSx }}
          >
            {backButtonText}
          </Button>
          <div style={{ flex: "1 1 auto" }} />
          <Button
            onClick={activeStep === steps.length - 1 ? handleComplete : handleNext}
            style={buttonSx}
            disabled={!canProceed || isProcessing} // Disable Next button if canProceed is false or processing
          >
            {activeStep === steps.length - 1 ? finishButtonText : nextButtonText}
          </Button>
        </div>
      </div>
    );
  }
);

AutoWizard.displayName = "AutoWizard";

export default AutoWizard;
